import * as THREE from 'three';

export const TAG_SLOTS = 18;
export const TAG_HOME = Math.PI / 2;
export const TAG_ARC_OUTER = Math.PI * 0.92;
export const TAG_ARC_INNER = Math.PI * 0.82;
export const TAG_RIM = 0.4;

/** Tight hull of the real nodes — not the reserved smoke box. */
export function titleContentRadius(points, cx, cy, pad = 0.4) {
  let span = 0;
  (points || []).forEach(([px, py]) => {
    span = Math.max(span, Math.hypot(px - cx, py - cy));
  });
  return span + pad;
}

/** Axis-aligned box around disks. A tall island's top is maxY, not the circumcircle. */
export function diskAabb(points) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  (points || []).forEach((p) => {
    const r = p.r || 0;
    minX = Math.min(minX, p.x - r);
    maxX = Math.max(maxX, p.x + r);
    minY = Math.min(minY, p.y - r);
    maxY = Math.max(maxY, p.y + r);
  });
  if (!Number.isFinite(minX)) return { cx: 0, cy: 0, hw: 0, hh: 0 };
  return {
    cx: (minX + maxX) * 0.5,
    cy: (minY + maxY) * 0.5,
    hw: (maxX - minX) * 0.5,
    hh: (maxY - minY) * 0.5,
  };
}

function cross2h(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Monotone-chain hull, CCW. */
export function convexHull(points) {
  const pts = (points || [])
    .map((p) => ({ x: p.x, y: p.y }))
    .sort((a, b) => a.x - b.x || a.y - b.y);
  const uniq = [];
  pts.forEach((p) => {
    const last = uniq[uniq.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) uniq.push(p);
  });
  if (uniq.length <= 2) return uniq;
  const lower = [];
  uniq.forEach((p) => {
    while (lower.length >= 2 && cross2h(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  });
  const upper = [];
  for (let i = uniq.length - 1; i >= 0; i -= 1) {
    const p = uniq[i];
    while (upper.length >= 2 && cross2h(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function pointInConvexHull(x, y, hull, eps = 1e-6) {
  if (!hull || hull.length < 3) return false;
  let sign = 0;
  for (let i = 0; i < hull.length; i += 1) {
    const a = hull[i];
    const b = hull[(i + 1) % hull.length];
    const c = (b.x - a.x) * (y - a.y) - (b.y - a.y) * (x - a.x);
    if (Math.abs(c) <= eps) continue;
    const s = c > 0 ? 1 : -1;
    if (!sign) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/** Seats just outside the hull so nodes cannot wrap around the title. */
export function hullOutwardSeats(hull, gap) {
  const seats = [];
  if (!hull?.length) return seats;
  if (hull.length === 1) {
    for (let i = 0; i < TAG_SLOTS; i += 1) {
      const a = (i / TAG_SLOTS) * Math.PI * 2;
      seats.push({ x: hull[0].x + Math.cos(a) * gap, y: hull[0].y + Math.sin(a) * gap, angle: a });
    }
    return seats;
  }
  const n = hull.length;
  for (let i = 0; i < n; i += 1) {
    const a = hull[i];
    const b = hull[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;
    const ang = Math.atan2(ny, nx);
    seats.push({
      x: (a.x + b.x) * 0.5 + nx * gap,
      y: (a.y + b.y) * 0.5 + ny * gap,
      angle: ang,
    });
    seats.push({ x: a.x + nx * gap, y: a.y + ny * gap, angle: ang });
  }
  return seats;
}

/** Own-cluster disks/names may graze; foreign ones stay hard blockers. */
export function titleOccluderWeight(hitKey, ownKey, ownScale = 0.08) {
  return hitKey && ownKey && hitKey === ownKey ? ownScale : 1;
}

/** Persistent names win — a title sitting on one must walk. */
export function titleBoxHitsNames(box, names, pad = 10) {
  if (!box || !names?.length) return false;
  for (let i = 0; i < names.length; i += 1) {
    const other = names[i];
    if (!other) continue;
    const x = Math.max(0, Math.min(box.left + box.w, other.left + other.w + pad) - Math.max(box.left, other.left - pad));
    const y = Math.max(0, Math.min(box.top + box.h, other.top + other.h + pad) - Math.max(box.top, other.top - pad));
    if (x > 0 && y > 0 && (x * y) / Math.max(box.w * box.h, 1) > 0.02) return true;
  }
  return false;
}

/** Nebula titles hang off the island roots, not nested children. */
export function isNebulaTitleRoot(meta) {
  return (meta?.nestDepth ?? 0) === 0 && !meta?.parentTaskId;
}

/**
 * Cartographic pick: miss disks first, then names, then hug the hull.
 * Dirty seats only win by being much cleaner.
 */
export function closerTitleSeat(cur, next, stick = 6) {
  if (!next || !Number.isFinite(next.score)) return false;
  if (!cur || !Number.isFinite(cur.score)) return true;
  const curCover = cur.cover ?? ((cur.touch ?? 0) > 0.002 ? 1 : 0);
  const nextCover = next.cover ?? ((next.touch ?? 0) > 0.002 ? 1 : 0);
  if (curCover && !nextCover) return true;
  if (!curCover && nextCover) return false;
  const curName = cur.nameCover ?? 0;
  const nextName = next.nameCover ?? 0;
  if (curName && !nextName) return true;
  if (!curName && nextName) return false;
  const curTouch = cur.touch ?? 0;
  const nextTouch = next.touch ?? 0;
  const curDirty = curTouch > 0.002;
  const nextDirty = nextTouch > 0.002;
  if (curDirty && !nextDirty) return true;
  if (!curDirty && nextDirty) return false;
  if (!curDirty && !nextDirty && next.rimK < (cur.rimK ?? 1) - 0.03) return true;
  return next.score < cur.score - stick;
}

/** Step out along a hull normal until `blocked` is false — the usual map-label push. */
export function pushAlongNormal(ox, oy, nx, ny, gaps, blocked) {
  for (let i = 0; i < gaps.length; i += 1) {
    const g = gaps[i];
    const x = ox + nx * g;
    const y = oy + ny * g;
    if (!blocked(x, y)) return { x, y, gap: g, angle: Math.atan2(ny, nx) };
  }
  const g = gaps[gaps.length - 1];
  return g == null ? null : { x: ox + nx * g, y: oy + ny * g, gap: g, angle: Math.atan2(ny, nx) };
}

export function wrapAngle(a) {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

export function rimLocal(bunch, angle) {
  const ox = bunch.userData.contentOffX ?? 0;
  const oy = bunch.userData.contentOffY ?? 0;
  const hw = (bunch.userData.contentW ?? bunch.userData.halfW ?? 2) + TAG_RIM;
  const hh = (bunch.userData.contentH ?? bunch.userData.halfH ?? 2) + TAG_RIM;
  return { x: ox + Math.cos(angle) * hw, y: oy + Math.sin(angle) * hh, z: bunch.userData.tagZ ?? 0.45 };
}

export function labelOverlap(ax, ay, lw, lh, node) {
  const closestX = THREE.MathUtils.clamp(node.x, ax - lw * 0.5, ax + lw * 0.5);
  const closestY = THREE.MathUtils.clamp(node.y, ay, ay + lh);
  const dx = node.x - closestX;
  const dy = node.y - closestY;
  const d = Math.hypot(dx, dy);
  const need = node.r + (node.pad ?? 0);
  return d < need ? need - d : 0;
}

export function otherClusterPull(group, bunch, wx, wy) {
  const own = Math.hypot(wx - bunch.position.x, wy - bunch.position.y);
  let pull = 0;
  for (let i = 0; i < group.children.length; i += 1) {
    const other = group.children[i];
    if (other === bunch || !other.userData?.tag) continue;
    const d = Math.hypot(wx - other.position.x, wy - other.position.y);
    if (d < own) pull += (own - d) * 4;
    const otherR = other.userData.contentW ?? Math.max(0.5, (other.userData.halfW || 2) * 0.3);
    const selfR = bunch.userData.contentW ?? Math.max(0.5, (bunch.userData.halfW || 2) * 0.3);
    const keep = otherR + selfR * 0.25;
    if (d < keep) pull += keep - d;
  }
  return pull;
}

/** True if a projected disk overlaps the NDC viewport. */
export function nodeInView(ndcX, ndcY, ndcR = 0) {
  return (ndcX + ndcR) > -1 && (ndcX - ndcR) < 1
    && (ndcY + ndcR) > -1 && (ndcY - ndcR) < 1;
}

/** Cluster keys that have at least one on-screen node (already projected). */
export function visibleClusterKeysFromNodes(nodes) {
  const keys = new Set();
  (nodes || []).forEach((n) => {
    if (!n?.key || n.key === '__none__') return;
    if (n.z < -1 || n.z > 1) return;
    if (nodeInView(n.x, n.y, n.r || 0)) keys.add(n.key);
  });
  return keys;
}

export function taggedBunches(group) {
  const out = [];
  group.children.forEach((b) => { if (b.userData?.tag) out.push(b); });
  return out;
}

export function clusterCentroid(bunches) {
  let sx = 0;
  let sy = 0;
  bunches.forEach((b) => { sx += b.position.x; sy += b.position.y; });
  const n = Math.max(bunches.length, 1);
  return { x: sx / n, y: sy / n };
}

function cross2(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Clusters on the convex hull — they own the open space outside the graph. */
export function outerClusterSet(bunches) {
  const pts = bunches.map((b) => ({ b, x: b.position.x, y: b.position.y }));
  if (pts.length <= 2) return new Set(bunches);
  pts.sort((a, c) => a.x - c.x || a.y - c.y);
  const lower = [];
  pts.forEach((p) => {
    while (lower.length >= 2 && cross2(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  });
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const p = pts[i];
    while (upper.length >= 2 && cross2(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  const set = new Set();
  lower.slice(0, -1).concat(upper.slice(0, -1)).forEach((p) => set.add(p.b));
  return set;
}

export function outwardAngle(bunch, mid) {
  const dx = bunch.position.x - mid.x;
  const dy = bunch.position.y - mid.y;
  if (dx * dx + dy * dy < 0.04) return TAG_HOME;
  return Math.atan2(dy, dx);
}

/** Emptiest sector around an inner island — away from every neighbor. */
export function emptiestGapAngle(bunch, bunches) {
  const angles = [];
  bunches.forEach((other) => {
    if (other === bunch) return;
    angles.push(Math.atan2(other.position.y - bunch.position.y, other.position.x - bunch.position.x));
  });
  if (!angles.length) return TAG_HOME;
  angles.sort((a, b) => a - b);
  let bestGap = -1;
  let best = TAG_HOME;
  for (let i = 0; i < angles.length; i += 1) {
    const a = angles[i];
    const b = angles[(i + 1) % angles.length];
    const gap = i === angles.length - 1 ? b + Math.PI * 2 - a : b - a;
    if (gap > bestGap) {
      bestGap = gap;
      best = wrapAngle(a + gap * 0.5);
    }
  }
  return best;
}

export function towardNeighborCost(bunch, bunches, angle) {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  let cost = 0;
  bunches.forEach((other) => {
    if (other === bunch) return;
    const ox = other.position.x - bunch.position.x;
    const oy = other.position.y - bunch.position.y;
    const len = Math.hypot(ox, oy) || 1;
    const align = (ox * dx + oy * dy) / len;
    if (align > 0.12) cost += (align * align) / Math.max(len, 0.8);
  });
  return cost;
}

/** Liang–Barsky: true if the segment crosses the padded label box. */
export function segHitsAabb(x1, y1, x2, y2, left, right, bottom, top) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  let t0 = 0;
  let t1 = 1;
  const clip = (p, q) => {
    if (Math.abs(p) < 1e-9) return q >= 0;
    const u = q / p;
    if (p < 0) {
      if (u > t1) return false;
      if (u > t0) t0 = u;
    } else {
      if (u < t0) return false;
      if (u < t1) t1 = u;
    }
    return true;
  };
  return clip(-dx, x1 - left) && clip(dx, right - x1) && clip(-dy, y1 - bottom) && clip(dy, top - y1);
}

export function wireOverlap(ax, ay, lw, lh, seg, pad) {
  const left = ax - lw * 0.5;
  const right = ax + lw * 0.5;
  const bottom = ay;
  const top = ay + lh;
  if (segHitsAabb(seg.x1, seg.y1, seg.x2, seg.y2, left - pad, right + pad, bottom - pad, top + pad)) {
    return 1.35;
  }
  return 0;
}

/** Start pulling a title back only after it is clearly off — grazes must not twitch. */
export const TITLE_VIEW_ENTER = 0.03;

export function titleShouldPullOnView(off, alreadyPulling = false) {
  if (off <= 1e-4) return false;
  if (alreadyPulling) return true;
  return off > TITLE_VIEW_ENTER;
}

/** How far a hanging label (anchor at bottom-center) sits outside the usable view. */
export function labelOffView(ndcX, ndcY, lw, lh, topInset = 0, leftInset = 0) {
  const left = ndcX - lw * 0.5;
  const right = ndcX + lw * 0.5;
  const bottom = ndcY;
  const top = ndcY + lh;
  const minX = -1 + leftInset;
  const maxY = 1 - topInset;
  let off = 0;
  if (left < minX) off += minX - left;
  if (right > 1) off += right - 1;
  if (bottom < -1) off += -1 - bottom;
  if (top > maxY) off += top - maxY;
  return off;
}

/** Viewport inset so the whole hanging label stays on screen, below the toolbar fade. */
export function viewRectForLabel(lw, lh, pad = 0, topInset = 0, leftInset = 0) {
  return {
    left: -1 + leftInset + lw * 0.5 + pad,
    right: 1 - lw * 0.5 - pad,
    bottom: -1 + pad,
    top: 1 - topInset - lh - pad,
  };
}

/** A better title seat must win this many frames before the name walks. */
export const TITLE_SEAT_CONFIRM = 8;
/** After a confirmed walk, keep the world seat so orbit cannot hop it. */
export const TITLE_SEAT_LOCK = 40;

/** Same Schmitt trigger as parent names — quantized so orbit jitter does not reset. */
export function titleSeatSwitchReady(ud, wx, wy, need = TITLE_SEAT_CONFIRM) {
  const qx = Math.round((wx ?? 0) * 20) / 20;
  const qy = Math.round((wy ?? 0) * 20) / 20;
  const same = ud.wantTitleX === qx && ud.wantTitleY === qy;
  if (!same) {
    ud.wantTitleX = qx;
    ud.wantTitleY = qy;
    ud.wantTitleHold = 1;
    return need <= 1;
  }
  ud.wantTitleHold = (ud.wantTitleHold ?? 0) + 1;
  return ud.wantTitleHold >= need;
}

export function clearTitleSeatWant(ud) {
  ud.wantTitleX = null;
  ud.wantTitleY = null;
  ud.wantTitleHold = 0;
}

/**
 * Visible seat for a title. Prefer the cloud ∩ usable view. If the cloud
 * is on the canvas but only in the toolbar band, pin a sliver on the
 * view edge — a tagged island that is on-screen must keep its name.
 */
export function titleSlice(aabb, view, center) {
  const hit = aabb ? intersectRects(aabb, view) : null;
  if (hit) return hit;
  const onCanvas = (p) => p && p.x > -1.05 && p.x < 1.05 && p.y > -1.05 && p.y < 1.05;
  const aabbOnCanvas = aabb
    && aabb.right > -1.05 && aabb.left < 1.05
    && aabb.top > -1.05 && aabb.bottom < 1.05;
  if (!aabbOnCanvas && !onCanvas(center)) return null;
  const p = clampPointToRect(center?.x ?? 0, center?.y ?? 0, view);
  const pad = 0.08;
  return {
    left: Math.max(view.left, p.x - pad),
    right: Math.min(view.right, p.x + pad),
    bottom: Math.max(view.bottom, p.y - pad),
    top: Math.min(view.top, p.y + pad),
  };
}

export function intersectRects(a, b) {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  const top = Math.min(a.top, b.top);
  if (right < left || top < bottom) return null;
  return { left, right, bottom, top };
}

export function clampPointToRect(x, y, rect) {
  return {
    x: Math.min(rect.right, Math.max(rect.left, x)),
    y: Math.min(rect.top, Math.max(rect.bottom, y)),
  };
}

export function easeRect(prev, next, k = 0.2) {
  if (!next) return prev;
  if (!prev) return { left: next.left, right: next.right, bottom: next.bottom, top: next.top };
  return {
    left: prev.left + (next.left - prev.left) * k,
    right: prev.right + (next.right - prev.right) * k,
    bottom: prev.bottom + (next.bottom - prev.bottom) * k,
    top: prev.top + (next.top - prev.top) * k,
  };
}

export function pointInRect(x, y, rect) {
  return x >= rect.left && x <= rect.right && y >= rect.bottom && y <= rect.top;
}

/** Closest on-screen seat to the last place; else the least-clipped one. */
export function pickVisibleSeat(seats, preferX, preferY) {
  let on = null;
  let onD = Infinity;
  let least = null;
  for (let i = 0; i < seats.length; i += 1) {
    const s = seats[i];
    if (s.off <= 1e-4) {
      const dx = s.x - preferX;
      const dy = s.y - preferY;
      const d = dx * dx + dy * dy;
      if (d < onD) {
        on = s;
        onD = d;
      }
    }
    if (!least || s.off < least.off) least = s;
  }
  return on || least;
}
